import { ITreeNodeInstance } from '@infotech-soe/merps/types/Tree';

import IApiProvider from '../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import {
  CategoryName,
  DbId,
  IPrivsCatalogsTreeAction,
  IPrivsTreeAction,
  TAffiliationSign,
  TRoleId,
} from '../../models/role-privs/RolePrivsData';

import IRolePrivsService from './IRolePrivsService';

class RolePrivsServiceSingleton implements IRolePrivsService {
  private readonly apiProvider: IApiProvider;
  constructor() {
    this.apiProvider = new MerpsApiProvider();
  }

  public loadPanes(categoryName: CategoryName): Promise<TAffiliationSign[]> {
    return this.rolePrivsApi.getPanes(categoryName);
  }

  public loadTree(
    categoryName: CategoryName,
    roleId: TRoleId,
    treeType: TAffiliationSign,
    nodeId?: DbId
  ): Promise<ITreeNodeInstance[]> {
    return this.rolePrivsApi.getTree(categoryName, roleId, treeType, nodeId);
  }

  public loadCategoryTree(
    categoryName: CategoryName,
    nodeId?: DbId
  ): Promise<ITreeNodeInstance[]> {
    return this.rolePrivsApi.getCategoryTree(categoryName, nodeId);
  }

  public loadCatalogActions(
    categoryName: CategoryName,
    roleId: TRoleId,
    catalogId: DbId
  ): Promise<IPrivsCatalogsTreeAction[]> {
    return this.rolePrivsApi.getCatalogActions(categoryName, roleId, catalogId);
  }

  public loadCategoryActions(
    categoryName: CategoryName,
    roleId: TRoleId,
    nodeIds: Partial<Record<TAffiliationSign, DbId>>
  ): Promise<IPrivsTreeAction[]> {
    return this.rolePrivsApi.getCategoryActions(categoryName, roleId, nodeIds);
  }

  public setTreeNodePrivs(
    categoryName: CategoryName,
    roleId: TRoleId,
    pane: TAffiliationSign,
    nodeId: DbId,
    grantFlag: boolean,
    inheritHier: boolean
  ): Promise<void> {
    return this.rolePrivsApi.setMainTreeNodePrivs(
      categoryName,
      roleId,
      pane,
      nodeId,
      grantFlag,
      inheritHier
    );
  }

  public setCatalogActionPrivs(
    categoryName: CategoryName,
    roleId: TRoleId,
    nodeId: DbId,
    funcCode: string,
    grantFlag: boolean,
    inheritHier: boolean
  ): Promise<void> {
    return this.rolePrivsApi.setCatalogActionPrivs(
      categoryName,
      roleId,
      nodeId,
      funcCode,
      grantFlag,
      inheritHier
    );
  }

  public setCategoryActionPrivs(
    categoryName: CategoryName,
    roleId: TRoleId,
    nodeIds: Partial<Record<TAffiliationSign, DbId>>,
    funcCode: string,
    grantFlag: boolean,
    inheritHier: boolean
  ): Promise<void> {
    return this.rolePrivsApi.setCategoryActionPrivs(
      categoryName,
      roleId,
      nodeIds,
      funcCode,
      grantFlag,
      inheritHier
    );
  }

  public setCatalogAllActionsPrivs(
    categoryName: CategoryName,
    roleId: TRoleId,
    catalogId: DbId,
    inheritHier: boolean
  ): Promise<void> {
    return this.rolePrivsApi.setCatalogAllActionsPrivs(
      categoryName,
      roleId,
      catalogId,
      inheritHier
    );
  }

  public setCategoryAllActionsPrivs(
    categoryName: CategoryName,
    roleId: TRoleId,
    nodeIds: Partial<Record<TAffiliationSign, DbId>>,
    inheritHier: boolean
  ): Promise<void> {
    return this.rolePrivsApi.setCategoryAllActionsPrivs(
      categoryName,
      roleId,
      nodeIds,
      inheritHier
    );
  }

  private get rolePrivsApi() {
    return this.apiProvider.rolePrivs;
  }
}

const RolePrivsService = (() => {
  let instance: IRolePrivsService;
  return new Proxy(RolePrivsServiceSingleton, {
    construct(Target: typeof RolePrivsServiceSingleton): IRolePrivsService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default RolePrivsService;
